import React, { useEffect, useMemo, useState } from 'react';
import {
  getAuth,
  signOut,
  updateProfile,
  onIdTokenChanged,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  getIdTokenResult,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot,
  deleteDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  writeBatch,
  serverTimestamp,
  runTransaction,
  increment,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  Copy,
  CreditCard,
  KeyRound,
  LogOut,
  Search,
  ShieldAlert,
  Trash2,
  UserCircle2,
  Users,
} from 'lucide-react';
import { functions } from '../firebase/firebase';
import { logEventSafe } from '../analytics/analytics';
import { PageBackLink, PagePanel, PageShell } from './MarketingPageShell';

const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
const SEAT_PRICE = 6.5;

const inputClassName =
  'w-full rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2.5 text-white placeholder:text-gray-500 transition focus:border-blue-400/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30';
const panelCardClassName = 'rounded-xl border border-white/10 bg-white/[0.03] p-3.5';
const buttonPrimaryClassName =
  'inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 font-medium text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50';
const buttonSecondaryClassName =
  'inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 font-medium text-white transition hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-50';
const buttonDangerClassName =
  'inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2.5 font-medium text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50';

const MessageText = ({ type = 'error', children }) => (
  <p className={`text-sm ${type === 'error' ? 'text-red-300' : 'text-emerald-300'}`}>{children}</p>
);

const LoadingLine = ({ className = 'w-28' }) => (
  <span className={`block h-4 animate-pulse rounded bg-white/10 ${className}`} />
);

const SectionHeading = ({ icon: Icon, title, description }) => (
  <div>
    <div className="flex items-center gap-2 text-white">
      <Icon className="h-4 w-4 text-blue-200" />
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
    {description ? <p className="mt-1 text-sm text-gray-400">{description}</p> : null}
  </div>
);

const StatCard = ({ label, value, accent = 'text-white', loading = false }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
    <div className="text-xs uppercase tracking-[0.24em] text-gray-500">{label}</div>
    <div className={`mt-1.5 text-sm font-medium ${accent}`}>
      {loading ? <LoadingLine className="w-24" /> : value}
    </div>
  </div>
);

const AccountPage = () => {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    logEventSafe('page_view', {
      page_path: location.pathname,
      page_title: 'Account Overview',
    });
  }, [location.pathname]);

  const [user, setUser] = useState(null);
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [nameUpdateMsg, setNameUpdateMsg] = useState('');
  const [nameError, setNameError] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [canUpdateName, setCanUpdateName] = useState(true);
  const [nextNameUpdate, setNextNameUpdate] = useState(null);

  const [orgId, setOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgInviteKey, setOrgInviteKey] = useState('');
  const [orgUpdateMsg, setOrgUpdateMsg] = useState('');
  const [orgError, setOrgError] = useState('');
  const [updatingOrg, setUpdatingOrg] = useState(false);
  const [canUpdateOrg, setCanUpdateOrg] = useState(true);
  const [nextOrgUpdate, setNextOrgUpdate] = useState(null);

  const [memberLimit, setMemberLimit] = useState(0);
  const [limitUpdateMsg, setLimitUpdateMsg] = useState('');
  const [limitError, setLimitError] = useState('');
  const [updatingLimit, setUpdatingLimit] = useState(false);

  const [subscription, setSubscription] = useState({
    plan: 'Standard',
    status: 'Not available',
    renewalDate: 'Not available',
    paymentMethod: 'Not available',
  });
  const [managingSub, setManagingSub] = useState(false);

  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [reauthPassword, setReauthPassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const [joinOrgVisible, setJoinOrgVisible] = useState(false);
  const [inviteKey, setInviteKey] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joinInfo, setJoinInfo] = useState('');
  const [userRole, setUserRole] = useState('');
  const [members, setMembers] = useState([]);

  const [copySuccess, setCopySuccess] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([]);
  const itemsPerPage = 10;

  const [searchInput, setSearchInput] = useState(searchTerm);

  const checkNameUpdateAvailability = (uid) => {
    const key = `displayNameLastUpdate_${uid}`;
    const last = localStorage.getItem(key);
    if (last) {
      const next = parseInt(last, 10) + ONE_MONTH_MS;
      if (Date.now() < next) {
        setCanUpdateName(false);
        setNextNameUpdate(new Date(next).toLocaleDateString());
      }
    }
  };

  const recordNameUpdate = (uid) => {
    localStorage.setItem(`displayNameLastUpdate_${uid}`, Date.now().toString());
    checkNameUpdateAvailability(uid);
  };

  const checkOrgUpdateAvailability = (id) => {
    const key = `orgNameLastUpdate_${id}`;
    const last = localStorage.getItem(key);
    if (last) {
      const next = parseInt(last, 10) + ONE_MONTH_MS;
      if (Date.now() < next) {
        setCanUpdateOrg(false);
        setNextOrgUpdate(new Date(next).toLocaleDateString());
      }
    }
  };

  const recordOrgUpdate = (id) => {
    localStorage.setItem(`orgNameLastUpdate_${id}`, Date.now().toString());
    checkOrgUpdateAvailability(id);
  };

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (u) => {
      setIsAuthResolved(true);
      if (!u) {
        navigate('/');
        return;
      }
      setUser(u);
      setDisplayName(u.displayName || '');
      checkNameUpdateAvailability(u.uid);
      try {
        const tokenResult = await getIdTokenResult(u, true);
        setSubscription((prev) => ({
          ...prev,
          plan: tokenResult.claims.stripeRole || prev.plan,
        }));
      } catch {
        // ignore
      }
    });
    return unsubscribe;
  }, [auth, navigate]);

  useEffect(() => {
    if (!user) {
      setIsSubscriptionLoading(false);
      return;
    }
    (async () => {
      setIsSubscriptionLoading(true);
      try {
        const subsRef = collection(db, 'customers', user.uid, 'subscriptions');
        const subsQ = query(subsRef, orderBy('created', 'desc'), limit(1));
        const snap = await getDocs(subsQ);
        if (!snap.empty) {
          const data = snap.docs[0].data();
          const nickname = data.items?.data?.[0]?.price?.nickname;
          const status = data.status;
          const renewal = data.current_period_end
            ? new Date(data.current_period_end.seconds * 1000).toLocaleDateString()
            : 'Not available';
          let pm = 'Not available';
          if (data.default_payment_method?.card) {
            const { brand, last4 } = data.default_payment_method.card;
            pm = `${brand[0].toUpperCase() + brand.slice(1)} ending in ${last4}`;
          }
          setSubscription((prev) => ({
            ...prev,
            plan: nickname ?? prev.plan,
            status,
            renewalDate: renewal,
            paymentMethod: pm,
          }));
        }
      } catch {
        // ignore
      } finally {
        setIsSubscriptionLoading(false);
      }
    })();
  }, [user, db]);

  useEffect(() => {
    if (!user) {
      setIsProfileLoading(false);
      return;
    }
    (async () => {
      setIsProfileLoading(true);
      try {
        const uSnap = await getDoc(doc(db, 'users', user.uid));
        const uData = uSnap.data() || {};
        setUserRole(uData.role || '');

        if (uData.orgId) {
          const orgRef = doc(db, 'organizations', uData.orgId);
          const oSnap = await getDoc(orgRef);

          if (oSnap.exists()) {
            const oData = oSnap.data();
            setOrgId(uData.orgId);
            setOrgName(oData.name);
            setOrgInviteKey(oData.inviteKey);
            setMemberLimit(oData.memberLimit || 0);
            setJoinOrgVisible(false);
            checkOrgUpdateAvailability(uData.orgId);
          } else {
            setOrgId('');
            setOrgName('');
            setOrgInviteKey('');
          }
        } else {
          setOrgId('');
          setOrgName('');
          setOrgInviteKey('');
        }
      } catch {
        // ignore
      } finally {
        setIsProfileLoading(false);
      }
    })();
  }, [user, db]);

  useEffect(() => {
    if (userRole !== 'manager' || !orgId) {
      setMembers([]);
      return;
    }
    const membersCol = collection(db, 'organizations', orgId, 'members');
    const constraints = [];
    if (searchTerm) {
      constraints.push(where('email', '>=', searchTerm));
      constraints.push(where('email', '<=', `${searchTerm}\uf8ff`));
      constraints.push(orderBy('email'));
    } else {
      constraints.push(orderBy('joinedAt'));
    }
    constraints.push(limit(itemsPerPage));
    if (currentPage > 1 && pageCursors[currentPage - 2]) {
      constraints.push(startAfter(pageCursors[currentPage - 2]));
    }
    const memberQuery = query(membersCol, ...constraints);
    const unsubscribe = onSnapshot(memberQuery, (snap) => {
      const docs = snap.docs;
      const list = docs.map((d) => ({
        id: d.id,
        email: d.data().email,
        role: d.data().role,
      }));
      setMembers(list);
      const lastVisible = docs[docs.length - 1];
      if (currentPage > pageCursors.length) {
        setPageCursors((prev) => [...prev, lastVisible]);
      }
    });
    return unsubscribe;
  }, [db, orgId, userRole, searchTerm, currentPage, pageCursors]);

  useEffect(() => {
    setCurrentPage(1);
    setPageCursors([]);
  }, [searchTerm]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setNameError('');
    setNameUpdateMsg('');
    if (!displayName.trim()) {
      setNameError('Display name cannot be empty.');
      return;
    }
    if (displayName.trim().length > 30) {
      setNameError('Display name must be 30 characters or less.');
      return;
    }
    if (!canUpdateName) {
      setNameError(`Display name updates limited to once per month. Next update: ${nextNameUpdate}`);
      return;
    }
    try {
      setUpdatingName(true);
      await updateProfile(auth.currentUser, {
        displayName: displayName.trim(),
      });
      setNameUpdateMsg('Display name updated successfully!');
      recordNameUpdate(auth.currentUser.uid);
      logEventSafe('update_display_name', { page_path: location.pathname });
    } catch {
      setNameError('Failed to update display name.');
    } finally {
      setUpdatingName(false);
    }
  };

  const handleOrgUpdate = async (e) => {
    e.preventDefault();
    setOrgError('');
    setOrgUpdateMsg('');
    if (!orgName.trim()) {
      setOrgError('Organization name cannot be empty.');
      return;
    }
    if (orgName.trim().length > 30) {
      setOrgError('Organization name must be 30 characters or less.');
      return;
    }
    if (!canUpdateOrg) {
      setOrgError(`Organization name updates limited to once per month. Next update: ${nextOrgUpdate}`);
      return;
    }
    try {
      setUpdatingOrg(true);
      await updateDoc(doc(db, 'organizations', orgId), {
        name: orgName.trim(),
      });
      await updateProfile(auth.currentUser, {
        displayName: orgName.trim(),
      });
      setDisplayName(orgName.trim());
      setOrgUpdateMsg('Organization name updated successfully!');
      recordOrgUpdate(orgId);
      recordNameUpdate(auth.currentUser.uid);
      logEventSafe('update_organization_name', { page_path: location.pathname });
    } catch {
      setOrgError('Failed to update organization name.');
    } finally {
      setUpdatingOrg(false);
    }
  };

  const handleLimitUpdate = async (e) => {
    e.preventDefault();
    setLimitError('');
    setLimitUpdateMsg('');
    if (!memberLimit || memberLimit < members.length) {
      setLimitError(`Limit must be at least current member count (${members.length}).`);
      return;
    }
    try {
      setUpdatingLimit(true);
      await updateDoc(doc(db, 'organizations', orgId), {
        memberLimit,
      });
      setLimitUpdateMsg('Max members updated successfully!');
      logEventSafe('update_member_limit', {
        new_limit: memberLimit,
        page_path: location.pathname,
      });
    } catch {
      setLimitError('Failed to update max members.');
    } finally {
      setUpdatingLimit(false);
    }
  };

  const handleKickUser = async (memberId) => {
    setJoinError('');
    setJoinInfo('');
    try {
      await deleteDoc(doc(db, 'organizations', orgId, 'members', memberId));
      await updateDoc(doc(db, 'organizations', orgId), {
        memberCount: increment(-1),
      });
      await updateDoc(doc(db, 'users', memberId), {
        orgId: '',
        role: 'user',
      });
      setJoinInfo('Member removed successfully.');
    } catch {
      setJoinError('Failed to remove member.');
    }
  };

  const handleJoinTeam = async () => {
    setJoinError('');
    setJoinInfo('');
    logEventSafe('join_team_attempt', { page_path: location.pathname });

    if (!inviteKey.trim()) {
      setJoinError('Please enter a team key.');
      return;
    }

    try {
      await runTransaction(db, async (tx) => {
        const orgRef = doc(db, 'organizations', inviteKey);
        const orgSnap = await tx.get(orgRef);
        if (!orgSnap.exists()) {
          throw new Error('Team key not found.');
        }

        const { memberLimit = 0, memberCount = 0 } = orgSnap.data();
        if (memberCount >= memberLimit) {
          throw new Error(`Team is full (limit: ${memberLimit}).`);
        }

        tx.update(orgRef, { memberCount: memberCount + 1 });

        const memberRef = doc(db, 'organizations', inviteKey, 'members', user.uid);
        tx.set(memberRef, {
          uid: user.uid,
          email: user.email,
          role: 'member',
          joinedAt: serverTimestamp(),
        });

        const userRef = doc(db, 'users', user.uid);
        tx.update(userRef, {
          orgId: inviteKey,
          role: 'member',
        });
      });

      setJoinInfo('Successfully joined team!');
      setOrgName((await getDoc(doc(db, 'organizations', inviteKey))).data().name);
      setOrgInviteKey(inviteKey);
      setJoinOrgVisible(false);
      setInviteKey('');
    } catch (err) {
      const msg = err.message.includes('full')
        ? err.message
        : `Failed to join team. ${err.message}`;
      setJoinError(msg);
    }
  };

  const handleLeaveTeam = async () => {
    setJoinError('');
    setJoinInfo('');
    try {
      const uSnap = await getDoc(doc(db, 'users', user.uid));
      const currOrg = uSnap.data()?.orgId;
      if (!currOrg) {
        setJoinError('You are not currently in a team.');
        return;
      }
      await runTransaction(db, async (tx) => {
        const orgRef = doc(db, 'organizations', currOrg);
        const orgSnap = await tx.get(orgRef);
        if (!orgSnap.exists()) {
          throw new Error('Organization not found.');
        }
        const { memberCount = 0 } = orgSnap.data();

        tx.update(orgRef, { memberCount: memberCount - 1 });
        tx.delete(doc(db, 'organizations', currOrg, 'members', user.uid));
        tx.update(doc(db, 'users', user.uid), {
          orgId: '',
          role: 'user',
        });
      });

      setJoinInfo('Successfully left the team.');
      setOrgName('');
      setOrgInviteKey('');
      setMembers([]);
    } catch {
      setJoinError('Failed to leave team.');
    }
  };

  const handleManageSubscription = async () => {
    logEventSafe('click_manage_subscription', { page_path: location.pathname });

    setManagingSub(true);
    try {
      const fn = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      const { data } = await fn({ returnUrl: window.location.origin });
      if (data.url) {
        window.location.assign(data.url);
      } else {
        throw new Error();
      }
    } catch {
      alert('Failed to redirect to subscription portal. Please contact support.');
    } finally {
      setManagingSub(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    if (deleteConfirmation !== 'DELETE') return;

    if (
      !window.confirm(
        'Are you sure you want to delete your account? This will cancel any active subscription and cannot be undone.'
      )
    ) {
      return;
    }

    try {
      const cred = EmailAuthProvider.credential(user.email, reauthPassword);
      await reauthenticateWithCredential(auth.currentUser, cred);

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data() || {};
      const currOrg = userData.orgId;
      const currRole = userData.role;

      if (currOrg && currRole !== 'manager') {
        await runTransaction(db, async (tx) => {
          const orgRef = doc(db, 'organizations', currOrg);
          const orgSnap = await tx.get(orgRef);
          if (!orgSnap.exists()) {
            throw new Error('Organization not found.');
          }
          const { memberCount = 0 } = orgSnap.data();

          tx.update(orgRef, { memberCount: memberCount - 1 });
          tx.delete(doc(db, 'organizations', currOrg, 'members', user.uid));
          tx.update(userRef, { orgId: '', role: 'user' });
        });
      }

      const batch = writeBatch(db);

      if (currOrg && currRole === 'manager') {
        const membersSnap = await getDocs(collection(db, 'organizations', currOrg, 'members'));
        membersSnap.forEach((m) => {
          batch.delete(doc(db, 'organizations', currOrg, 'members', m.id));
        });
        batch.delete(doc(db, 'organizations', currOrg));
      }

      batch.delete(userRef);
      await batch.commit();

      const delFn = httpsCallable(functions, 'deleteCustomerData');
      await delFn({ uid: user.uid });
      await deleteUser(auth.currentUser);

      logEventSafe('delete_account_success', { page_path: location.pathname });

      navigate('/');
    } catch (err) {
      console.error('handleDeleteAccount error:', err);
      setDeleteError('Failed to delete account. Please check your password and try again.');
    }
  };

  const handleSignOut = async () => {
    logEventSafe('sign_out', { page_path: location.pathname });
    await signOut(auth);
    navigate('/');
  };

  const handleCopyInviteKey = () => {
    navigator.clipboard
      .writeText(orgInviteKey)
      .then(() => {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(''), 2000);
      })
      .catch(() => {
        setCopySuccess('Failed to copy');
        setTimeout(() => setCopySuccess(''), 2000);
      });
  };

  const roleLabel = useMemo(() => {
    if (userRole === 'manager') return 'Team manager';
    if (userRole === 'member') return 'Team member';
    return 'Personal account';
  }, [userRole]);

  const teamLabel = useMemo(() => {
    if (orgName) return orgName;
    return userRole === 'manager' ? 'No team configured' : 'Not in a team';
  }, [orgName, userRole]);

  const accountDisplayLabel = useMemo(() => {
    if (userRole === 'manager' && orgName) return orgName;
    if (displayName?.trim()) return displayName.trim();
    return 'Not set';
  }, [displayName, orgName, userRole]);

  const subscriptionStatusColor =
    subscription.status === 'active'
      ? 'text-emerald-300'
      : subscription.status === 'Not available'
        ? 'text-gray-300'
        : 'text-amber-300';

  const isHeaderLoading = !isAuthResolved || isProfileLoading;
  const isPageBusy = !isAuthResolved || isProfileLoading || isSubscriptionLoading;

  return (
    <PageShell>
      <div className="space-y-4 py-3">
        <PageBackLink />

        <PagePanel className="space-y-4 p-4 sm:p-5">
          <div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-blue-200">Account</div>
              <h1 className="mt-1 text-xl font-semibold text-white sm:text-2xl">Account settings</h1>
              <p className="mt-1 text-sm text-gray-400">
                Manage profile details, billing, team access, and account security.
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Signed in as" value={accountDisplayLabel} loading={isHeaderLoading} />
            <StatCard label="Account type" value={roleLabel} accent="text-blue-200" loading={isHeaderLoading} />
            <StatCard label="Current plan" value={subscription.plan || 'Standard'} loading={!isAuthResolved || isSubscriptionLoading} />
            <StatCard label="Team" value={teamLabel} loading={isHeaderLoading} />
          </div>
        </PagePanel>

        <PagePanel className="space-y-4 p-4 sm:p-5">
          <div className="grid gap-4 xl:grid-cols-[1fr_0.95fr]">
            <div className="space-y-4">
            <SectionHeading
              icon={userRole === 'manager' ? Building2 : UserCircle2}
              title="Profile information"
              description="Update the name shown on this account."
            />

            <div className="grid gap-4 md:grid-cols-2">
              <div className={panelCardClassName}>
                <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Email</div>
                <div className="mt-3 text-base font-medium text-white">
                  {!isAuthResolved ? <LoadingLine className="w-40" /> : user?.email || 'Not available'}
                </div>
              </div>
              <div className={panelCardClassName}>
                <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Role</div>
                <div className="mt-3 text-base font-medium text-white">
                  {isHeaderLoading ? <LoadingLine className="w-28" /> : roleLabel}
                </div>
              </div>
            </div>

            {userRole === 'manager' ? (
              <form onSubmit={handleOrgUpdate} className="space-y-4">
                <div>
                  <label htmlFor="orgName" className="mb-2 block text-sm font-medium text-gray-200">
                    Organization Name
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    value={orgName}
                    maxLength={30}
                    onChange={(e) => setOrgName(e.target.value)}
                    className={inputClassName}
                    disabled={isPageBusy}
                  />
                  <p className="mt-2 text-sm text-gray-400">
                    Organization names are limited to 30 characters and can be updated once per month.
                  </p>
                </div>

                {orgError ? <MessageText>{orgError}</MessageText> : null}
                {orgUpdateMsg ? <MessageText type="success">{orgUpdateMsg}</MessageText> : null}
                {!canUpdateOrg && nextOrgUpdate ? (
                  <p className="text-sm text-gray-400">Next update available on {nextOrgUpdate}.</p>
                ) : null}

                <button
                  type="submit"
                  disabled={isPageBusy || updatingOrg || !canUpdateOrg}
                  className={buttonPrimaryClassName}
                >
                  {isPageBusy ? 'Loading...' : updatingOrg ? 'Updating...' : 'Save Organization Name'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <label htmlFor="displayName" className="mb-2 block text-sm font-medium text-gray-200">
                    Display Name
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    maxLength={30}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className={inputClassName}
                    disabled={isPageBusy}
                  />
                  <p className="mt-2 text-sm text-gray-400">
                    Display names are limited to 30 characters and can be updated once per month.
                  </p>
                </div>

                {nameError ? <MessageText>{nameError}</MessageText> : null}
                {nameUpdateMsg ? <MessageText type="success">{nameUpdateMsg}</MessageText> : null}
                {!canUpdateName && nextNameUpdate ? (
                  <p className="text-sm text-gray-400">Next update available on {nextNameUpdate}.</p>
                ) : null}

                <button
                  type="submit"
                  disabled={isPageBusy || updatingName || !canUpdateName}
                  className={buttonPrimaryClassName}
                >
                  {isPageBusy ? 'Loading...' : updatingName ? 'Updating...' : 'Save Display Name'}
                </button>
              </form>
            )}
            </div>

            <div className="space-y-4 border-t border-white/10 pt-4 xl:border-l xl:border-t-0 xl:pl-4 xl:pt-0">
            <SectionHeading
              icon={CreditCard}
              title="Subscription details"
              description="Review plan status and billing details."
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <StatCard label="Plan" value={subscription.plan} loading={!isAuthResolved || isSubscriptionLoading} />
              <StatCard label="Status" value={subscription.status} accent={subscriptionStatusColor} loading={!isAuthResolved || isSubscriptionLoading} />
              <StatCard label="Renewal date" value={subscription.renewalDate} loading={!isAuthResolved || isSubscriptionLoading} />
            </div>

            {userRole === 'manager' ? (
              <div className={panelCardClassName}>
                <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Plus team billing</div>
                <p className="mt-3 text-base text-gray-200">
                  ${(members.length * SEAT_PRICE).toFixed(2)} ({members.length} member{members.length !== 1 ? 's' : ''} x ${SEAT_PRICE.toFixed(2)}/user)
                </p>
              </div>
            ) : null}

            {subscription.plan !== 'Standard' && subscription.status === 'active' ? (
              <button
                onClick={handleManageSubscription}
                disabled={managingSub}
                className={buttonPrimaryClassName}
              >
                {managingSub ? 'Loading...' : 'Manage Subscription'}
              </button>
            ) : null}
            </div>
          </div>

          <div className="border-t border-white/10 pt-4 space-y-3">
          <SectionHeading
            icon={Users}
            title="Team management"
            description="Manage team access, limits, and members."
          />

          {joinError ? <MessageText>{joinError}</MessageText> : null}
          {joinInfo ? <MessageText type="success">{joinInfo}</MessageText> : null}

          {userRole === 'manager' && orgName ? (
            <div className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
                <div className={panelCardClassName}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Invite key</div>
                      <div className="mt-3 flex w-full items-center gap-3">
                        <div className="min-w-0 flex-1 rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2.5 font-mono text-sm text-gray-200">
                          {orgInviteKey}
                        </div>
                        <button
                          type="button"
                          onClick={handleCopyInviteKey}
                          className={`${buttonSecondaryClassName} shrink-0`}
                          aria-label="Copy invite key"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copy
                        </button>
                      </div>
                    </div>
                    {copySuccess ? <span className="text-sm text-emerald-300">{copySuccess}</span> : null}
                  </div>
                </div>

                <form onSubmit={handleLimitUpdate} className={`${panelCardClassName} space-y-4`}>
                  <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Member limit</div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <label htmlFor="memberLimit" className="mb-2 block text-sm font-medium text-gray-200">
                        Max Members
                      </label>
                      <input
                        id="memberLimit"
                        type="number"
                        min={members.length}
                        value={memberLimit}
                        onChange={(e) => setMemberLimit(parseInt(e.target.value, 10) || 0)}
                        className={inputClassName}
                      />
                    </div>
                    <button type="submit" disabled={updatingLimit} className={buttonPrimaryClassName}>
                      {updatingLimit ? 'Saving...' : 'Save Limit'}
                    </button>
                  </div>
                  {limitError ? <MessageText>{limitError}</MessageText> : null}
                  {limitUpdateMsg ? <MessageText type="success">{limitUpdateMsg}</MessageText> : null}
                </form>
              </div>

              <div className={panelCardClassName}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Team members</div>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {members.length} visible member{members.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="relative min-w-[16rem]">
                      <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search members..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="w-full rounded-2xl border border-white/10 bg-slate-950/60 py-3 pl-11 pr-4 text-white placeholder:text-gray-500 transition focus:border-blue-400/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setSearchTerm(searchInput)}
                      className={buttonSecondaryClassName}
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-slate-950/40">
                  {members.length > 0 ? (
                    <ul className="divide-y divide-white/10">
                      {members.map((member) => (
                        <li
                          key={member.id}
                          className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div>
                            <div className="text-sm font-medium text-white">{member.email}</div>
                            <div className="mt-1 text-xs uppercase tracking-[0.2em] text-gray-500">
                              {member.role}
                            </div>
                          </div>
                          {member.id !== user?.uid ? (
                            <button
                              type="button"
                              onClick={() => handleKickUser(member.id)}
                              className="text-sm font-medium text-red-300 transition hover:text-white"
                            >
                              Remove member
                            </button>
                          ) : (
                            <span className="text-sm text-gray-500">You</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-sm text-gray-400">No team members found.</div>
                  )}
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className={buttonSecondaryClassName}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-300">Page {currentPage}</span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={members.length < itemsPerPage}
                    className={buttonSecondaryClassName}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : orgName ? (
            <div className={`${panelCardClassName} flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between`}>
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Current team</div>
                <p className="mt-2 text-lg font-semibold text-white">{orgName}</p>
              </div>
              <button type="button" onClick={handleLeaveTeam} className={buttonSecondaryClassName}>
                Leave Team
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className={panelCardClassName}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Join a team</div>
                    <p className="mt-2 text-sm leading-6 text-gray-300">
                      Use an invite key from your manager to connect this account to an organization.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setJoinOrgVisible((visible) => !visible)}
                    className={buttonPrimaryClassName}
                  >
                    {joinOrgVisible ? 'Cancel' : 'Join a Team'}
                  </button>
                </div>
              </div>

              {joinOrgVisible ? (
                <div className={`${panelCardClassName} space-y-3`}>
                  <label htmlFor="inviteKey" className="mb-2 block text-sm font-medium text-gray-200">
                    Team Key
                  </label>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="relative flex-1">
                      <KeyRound className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                      <input
                        id="inviteKey"
                        type="text"
                        placeholder="Enter team key"
                        value={inviteKey}
                        onChange={(e) => setInviteKey(e.target.value)}
                        className="w-full rounded-2xl border border-white/10 bg-slate-950/60 py-3 pl-11 pr-4 text-white placeholder:text-gray-500 transition focus:border-blue-400/40 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                    <button type="button" onClick={handleJoinTeam} className={buttonPrimaryClassName}>
                      Submit
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
          </div>

          <div className="grid gap-4 border-t border-white/10 pt-4 xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] 2xl:grid-cols-1">
            <div className={`${panelCardClassName} space-y-4 self-start 2xl:max-w-md`}>
              <SectionHeading
                icon={LogOut}
                title="Session"
                description="End the current session."
              />
              <button type="button" onClick={handleSignOut} className={buttonSecondaryClassName}>
                Sign Out
              </button>
            </div>

            <div className="space-y-4">
              <SectionHeading
                icon={ShieldAlert}
                title="Danger zone"
                description="Delete this account permanently."
              />

              <div className="rounded-xl border border-red-400/20 bg-red-500/10 p-3.5">
                <div className="flex items-start gap-3">
                  <Trash2 className="mt-0.5 h-5 w-5 text-red-300" />
                  <div>
                    <p className="text-sm font-medium text-red-100">
                      Type DELETE in all caps, then confirm your password to permanently remove this account.
                    </p>
                    <p className="mt-2 text-sm leading-6 text-red-100/80">
                      This action cannot be undone. If you manage a team, the organization and member records are also removed.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleDeleteAccount} className="space-y-3" autoComplete="off">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="deleteConfirmation" className="mb-2 block text-sm font-medium text-gray-200">
                      Confirmation text
                    </label>
                    <input
                      id="deleteConfirmation"
                      type="text"
                      placeholder="Type DELETE to confirm"
                      autoComplete="off"
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label htmlFor="reauthPassword" className="mb-2 block text-sm font-medium text-gray-200">
                      Password
                    </label>
                    <input
                      id="reauthPassword"
                      type="password"
                      placeholder="Enter your password"
                      autoComplete="new-password"
                      value={reauthPassword}
                      onChange={(e) => setReauthPassword(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                </div>

                {deleteError ? <MessageText>{deleteError}</MessageText> : null}

                <button
                  type="submit"
                  disabled={deleteConfirmation !== 'DELETE'}
                  className={buttonDangerClassName}
                >
                  Delete Account
                </button>
              </form>
            </div>
          </div>
        </PagePanel>
      </div>
    </PageShell>
  );
};

export default AccountPage;
